import React, { useEffect, useState } from 'react';
import { MANAGED_STAGES, STATUS_FOR_STAGE, stageIndexOf, stageKeyOf } from '../../lib/managedStages';
import {
  createManagedOffer, fetchManagedEvents, addManagedEvent, advanceManagedStage, uploadManagedMedia,
  setManagedTracking, fetchOfferSourceSuppliers, isVerifiedSupplierStatus,
} from '../../lib/managedOrder';
import { getTrackingUrl, CARRIERS } from '../../lib/tracking';

// Admin operations for a managed order — the pipeline Maabar drives: advance the
// stages, present the single curated (anonymized) offer, upload the pre-shipping
// video, and post buyer-visible updates. Mirrors what the trader sees in the tracker.
//
// Nine of the ten stages are ours to advance. `confirmed` is the trader's: it is
// written by telr-verify when they pay the offer, and is rendered here as a
// disabled chip so an admin can see it without being able to set it.
const FB = "'Tajawal', sans-serif";
const inp = { width: '100%', padding: '8px 10px', border: '1px solid rgba(0,0,0,0.14)', borderRadius: 8, fontSize: 13, fontFamily: FB, boxSizing: 'border-box', background: '#fff' };
const lbl = { fontSize: 11, color: 'rgba(0,0,0,0.5)', fontFamily: FB, display: 'block', marginBottom: 3 };
const btn = { padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1a1814', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FB };
const card = { border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 16, marginBottom: 14, background: 'var(--bg-raised,#fff)' };
const h = { margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'rgba(0,0,0,0.75)', fontFamily: FB };
const errTxt = { fontSize: 11, color: '#c0392b', fontFamily: FB, margin: '3px 0 0' };
// Required-field marker — one style, reused (logical margin keeps it correct in RTL).
const req = { color: '#e07a6f', fontSize: 10, fontWeight: 700, marginInlineStart: 3 };
const Star = () => <span style={req} aria-hidden="true">*</span>;

// Offer validation: a trimmed, strictly-positive number (blank / whitespace / NaN fail).
const isPositive = (v) => {
  const s = String(v ?? '').trim();
  return s !== '' && Number.isFinite(Number(s)) && Number(s) > 0;
};

// ── What the trader will be told, per stage ─────────────────────────────────
//
// NOT the approved Meta copy, deliberately. The template bodies live in the
// provider console and are not mirrored here: a copy of those strings in the
// web app would drift from what Meta actually sends, silently, and nothing
// would reveal it until a trader received something we did not expect. These
// are OUR one-line descriptions of what the message MEANS, shown beside the
// template name and the real parameters — enough to know what you are about to
// send without pretending to quote it.
const STAGE_SUMMARY = {
  received: { ar: 'أننا استلمنا طلبه وبدأنا العمل عليه.', en: 'that we have their request and have started.' },
  sourcing: { ar: 'أننا بدأنا البحث والتفاوض مع المصانع.', en: 'that sourcing and negotiation have begun.' },
  offer: { ar: 'أن عرضه جاهز للمراجعة، مع عدد العروض.', en: 'that their offer is ready to review, with the offer count.' },
  production: { ar: 'أن الإنتاج بدأ.', en: 'that production has started.' },
  video: { ar: 'أن فيديو ما قبل الشحن متاح في صفحة الطلب.', en: 'that the pre-shipping video is on the order page.' },
  shipping: { ar: 'أن الشحنة غادرت.', en: 'that the shipment has left.' },
  customs: { ar: 'أن الشحنة في التخليص الجمركي.', en: 'that the shipment is in customs clearance.' },
  out_for_delivery: { ar: 'أن الشحنة خرجت للتوصيل، مع زر تتبّع.', en: 'that it is out for delivery, with a tracking button.' },
  delivered: { ar: 'أن الشحنة وصلت.', en: 'that the order has arrived.' },
};

export default function ManagedOpsPanel({
  requestId, managedStatus, isAr = true, onChanged,
  trackingNumber = '', shippingCompany = '', trackingUrl = '',
  // Shown in the confirmation dialog so an admin can see WHO they are about to
  // message and with WHAT. Optional: when a parent does not supply them the
  // dialog says the value is unknown rather than inventing one.
  traderName = '', requestRef = '',
}) {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => setEvents(await fetchManagedEvents(requestId));
  useEffect(() => { reload(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentIdx = stageIndexOf(managedStatus);

  // ── Shipment tracking (out_for_delivery only) ──
  // Optional: the stage stands on its own, so nothing here gates advancing.
  // Seeded from the saved values on the very first render — not only from the
  // effect below — so the inputs never paint empty for a frame before filling in.
  const [trk, setTrk] = useState({
    num: trackingNumber || '', carrier: shippingCompany || '', url: trackingUrl || '',
  });
  // Re-seed when the saved values change (after onChanged refetches the request),
  // so a save made here is reflected back rather than leaving stale local state.
  useEffect(() => {
    setTrk({ num: trackingNumber || '', carrier: shippingCompany || '', url: trackingUrl || '' });
  }, [trackingNumber, shippingCompany, trackingUrl]);

  const atDelivery = stageKeyOf(managedStatus) === 'out_for_delivery';
  const trkPreview = (trk.num || trk.url) ? getTrackingUrl(trk.carrier, trk.num, trk.url) : '';

  // Send only what actually changed against the saved values, so an untouched
  // field is never part of the UPDATE. Clearing a field on purpose still sends
  // it (it differs from what was saved) and stores NULL.
  const saveTracking = async () => {
    const patch = {};
    if (trk.num !== (trackingNumber || '')) patch.trackingNumber = trk.num;
    if (trk.carrier !== (shippingCompany || '')) patch.shippingCompany = trk.carrier;
    if (trk.url !== (trackingUrl || '')) patch.trackingUrl = trk.url;
    if (Object.keys(patch).length === 0) {
      setMsg(isAr ? 'لا تغيير للحفظ.' : 'Nothing changed.');
      return;
    }
    setBusy('tracking'); setMsg('');
    try {
      await setManagedTracking(requestId, patch);
      onChanged?.();
      setMsg(isAr ? '✓ حُفظ رقم التتبّع.' : '✓ Tracking saved.');
    } catch (e) { setMsg(e.message); }
    setBusy('');
  };

  // ── Advance stage ──
  // ONE call, because the status write and the event insert must not come
  // apart. They used to be setManagedStage() then addManagedEvent(), which
  // could never be atomic and, worse, lost the status write silently: RLS
  // matched no row, PostgREST returned 204, and this reported success while
  // managed_status never moved. The event still landed, so WhatsApp still
  // fired — the trader was told the order had advanced when the column said
  // otherwise. advance_managed_stage does both or neither.
  const advance = async (stg) => {
    setBusy('stage'); setMsg('');
    try {
      await advanceManagedStage({
        requestId, stage: stg.key, status: STATUS_FOR_STAGE[stg.key],
        title: isAr ? stg.ar : stg.en, kind: 'status',
      });
      await reload(); onChanged?.();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  };

  // ── Confirmation before advancing ──
  // A stage click is not an edit: it sends a WhatsApp message to a real trader
  // immediately, and it cannot be taken back. Worse, it cannot be corrected
  // either — wa_on_managed_event is forward-only and keyed on the stage, so
  // stepping back and advancing again enqueues NOTHING. One wrong click both
  // sends the wrong message and consumes the right one.
  //
  // Shape: { stg, file? }. A video upload is the same act — it advances to the
  // `video` stage and sends managed_video — so it goes through this same gate
  // rather than a second dialog that could drift from this one. The confirm
  // happens BEFORE the upload: the file is already chosen so its name can be
  // shown, and cancelling then costs nothing instead of leaving an orphan
  // object in the bucket.
  const [pending, setPending] = useState(null);

  /**
   * The template and the body parameters the DATABASE will build for it.
   *
   * The context is written by wa_on_managed_event and resolved positionally by
   * whatsapp-send/templates.ts; this mirrors the two specs an admin can reach.
   * Values known here are shown. The ones resolved at SEND time — the offer
   * count, and out_for_delivery's two button targets — are NAMED, not guessed:
   * a made-up number in a confirmation dialog is worse than an absent one.
   */
  const previewFor = (stg) => {
    const template = stg.key === 'out_for_delivery'
      ? ((trk.num || trk.url) ? 'managed_out_for_delivery' : 'managed_out_for_delivery_basic')
      : `managed_${stg.key}`;
    const body = [];
    // managed_received is the one template whose body opens with the name.
    if (stg.key === 'received') body.push(['trader_name', traderName]);
    body.push(['request_ref', requestRef]);
    if (stg.key === 'offer') body.push(['offer_count', null]);
    return { template, body };
  };

  /**
   * Whether this click will actually send.
   *
   * The real rule compares the target against the highest stage ALREADY SENT,
   * which lives in wa_messages.event_key and is not visible from here. This
   * compares against the CURRENT stage instead. The approximation can only
   * over-warn: if the status was corrected backwards, a click this calls
   * "will send" may in fact be silent. It never promises silence and then sends.
   */
  const willSend = (stg) => MANAGED_STAGES.findIndex((s) => s.key === stg.key) > currentIdx;

  // ── Present offer ──
  const [rows, setRows] = useState([{ desc: '', qty: '', unit_price: '' }]);
  // USD only (owner, 2026-10-06); create_managed_offer refuses anything else.
  const currency = 'USD';
  const [fp, setFp] = useState({ city: '', years: '', rating: '', capacity: '', export_markets: '', moq: '', lead_time: '', certs: '' });
  const [offerNotes, setOfferNotes] = useState('');
  // Which supplier the anonymised offer comes from — admin-only, never shown to
  // the trader. Only a VERIFIED source satisfies the request fee's 10-day promise.
  const [sourceId, setSourceId] = useState('');
  const [sources, setSources] = useState([]);
  useEffect(() => {
    let live = true;
    fetchOfferSourceSuppliers().then((r) => { if (live) setSources(r); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const source = sources.find((x) => x.id === sourceId) || null;
  const sourceVerified = Boolean(source && isVerifiedSupplierStatus(source.status));
  const [offerErr, setOfferErr] = useState({ rows: [] });
  const setRow = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const setF = (k, v) => setFp((p) => ({ ...p, [k]: v }));

  // Required: every row's item/qty/unit, the currency, and the factory location + MOQ.
  // The rest of the factory profile stays optional.
  const validateOffer = () => {
    const rowErrs = rows.map((r) => {
      const e = {};
      if (!String(r.desc ?? '').trim()) e.desc = isAr ? 'الوصف مطلوب' : 'Item is required';
      if (!String(r.qty ?? '').trim()) e.qty = isAr ? 'الكمية مطلوبة' : 'Qty is required';
      else if (!isPositive(r.qty)) e.qty = isAr ? 'يجب أن تكون الكمية رقمًا أكبر من صفر' : 'Qty must be a positive number';
      if (!String(r.unit_price ?? '').trim()) e.unit_price = isAr ? 'السعر مطلوب' : 'Unit is required';
      else if (!isPositive(r.unit_price)) e.unit_price = isAr ? 'يجب أن يكون السعر رقمًا أكبر من صفر' : 'Unit must be a positive number';
      return e;
    });
    const errs = { rows: rowErrs };
    if (!String(currency ?? '').trim()) errs.currency = isAr ? 'العملة مطلوبة' : 'Currency is required';
    if (!String(fp.city ?? '').trim()) errs.city = isAr ? 'الموقع مطلوب' : 'Location is required';
    if (!String(fp.moq ?? '').trim()) errs.moq = isAr ? 'أقل كمية مطلوبة' : 'MOQ is required';
    else if (!isPositive(fp.moq)) errs.moq = isAr ? 'يجب أن تكون أقل كمية رقمًا أكبر من صفر' : 'MOQ must be a positive number';
    return errs;
  };
  const hasOfferErr = (e) => Boolean(e.currency || e.city || e.moq || (e.rows || []).some((r) => Object.keys(r).length > 0));

  const presentOffer = async () => {
    const errs = validateOffer();
    setOfferErr(errs);
    if (hasOfferErr(errs)) {
      setMsg(isAr ? 'يرجى تعبئة الحقول المطلوبة قبل تقديم العرض.' : 'Please fill in the required fields before presenting the offer.');
      return;
    }
    if (!sourceVerified && !window.confirm(isAr
      ? 'المصدر غير محدد أو غير موثّق — هذا العرض لن يُحسب «مصنعًا موثّقًا» لرسوم الطلب، وقد تُستحق إعادة الرسوم للتاجر. متابعة؟'
      : 'No source, or an unverified one — this offer will NOT count as a verified factory for the request fee, and the fee may become refundable. Continue?')) return;
    setBusy('offer'); setMsg('');
    try {
      const lineItems = rows.filter((r) => r.desc || r.qty).map((r) => ({
        desc: r.desc || '', qty: Number(r.qty) || 0, unit_price: Number(r.unit_price) || 0,
        amount: (Number(r.qty) || 0) * (Number(r.unit_price) || 0),
      }));
      const factoryProfile = {
        city: fp.city || undefined, years: fp.years ? Number(fp.years) : undefined,
        rating: fp.rating ? Number(fp.rating) : undefined, capacity: fp.capacity || undefined,
        export_markets: fp.export_markets || undefined, moq: fp.moq ? Number(fp.moq) : undefined,
        lead_time: fp.lead_time || undefined,
        certifications: fp.certs ? fp.certs.split(/[،,]/).map((c) => c.trim()).filter(Boolean) : undefined,
      };
      await createManagedOffer({ requestId, lineItems, currency, factoryProfile, notes: offerNotes || null, sourceSupplierId: sourceId || null });
      setMsg(isAr ? '✓ تم تقديم العرض للتاجر.' : '✓ Offer presented to the trader.');
      onChanged?.();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  };

  // ── Pre-shipping video ──
  // Choosing a file no longer uploads: it opens the same confirmation the stage
  // buttons use, because this advances a stage and sends managed_video exactly
  // as they do. The input is cleared here so re-picking the same file still
  // fires a change event after a cancel.
  const onVideo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPending({ stg: MANAGED_STAGES.find((s) => s.key === 'video'), file });
  };

  const runVideo = async (file) => {
    setBusy('video'); setMsg('');
    try {
      const url = await uploadManagedMedia(requestId, file);
      // Same pair as advance(), and it had the same bug in the opposite order:
      // event first, then the status write that RLS discarded. One call now.
      // The upload stays separate — a stored file with no event is recoverable,
      // an event pointing at a file that was never uploaded is not.
      await advanceManagedStage({
        requestId, stage: 'video', status: STATUS_FOR_STAGE.video,
        title: isAr ? 'فيديو قبل الشحن' : 'Pre-shipping video',
        kind: 'video', mediaUrl: url,
      });
      await reload(); onChanged?.();
      setMsg(isAr ? '✓ رُفع الفيديو وظهر للتاجر.' : '✓ Video uploaded and shown to the trader.');
    } catch (err) { setMsg(err.message); }
    setBusy('');
  };

  // ── Add update ──
  const [upStage, setUpStage] = useState('production');
  const [upTitle, setUpTitle] = useState('');
  const [upBody, setUpBody] = useState('');
  const addUpdate = async () => {
    if (!upTitle.trim() && !upBody.trim()) return;
    setBusy('update'); setMsg('');
    try {
      await addManagedEvent({ requestId, stage: upStage, kind: 'note', title: upTitle.trim() || null, body: upBody.trim() || null });
      setUpTitle(''); setUpBody(''); await reload(); onChanged?.();
    } catch (e) { setMsg(e.message); }
    setBusy('');
  };

  return (
    <div dir={isAr ? 'rtl' : 'ltr'}>
      {msg && <div style={{ marginBottom: 12, fontSize: 12.5, color: msg.startsWith('✓') ? '#2d7a4f' : '#c0392b', fontFamily: FB }}>{msg}</div>}

      {/* Confirmation before a stage advance — see the note on `pending`. */}
      {pending && (() => {
        const stg = pending.stg;
        const { template, body } = previewFor(stg);
        const sends = willSend(stg);
        const summary = STAGE_SUMMARY[stg.key];
        return (
          <div role="dialog" aria-modal="true"
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={() => setPending(null)}>
            <div dir={isAr ? 'rtl' : 'ltr'} onClick={(ev) => ev.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, padding: 20, maxWidth: 480, width: '100%',
                fontFamily: FB, boxShadow: '0 18px 50px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700 }}>
                {pending.file
                  ? (isAr ? 'رفع فيديو ما قبل الشحن؟' : 'Upload the pre-shipping video?')
                  : (isAr ? `تقديم الطلب إلى «${stg.ar}»؟` : `Advance to "${stg.en}"?`)}
              </h3>

              {pending.file && (
                <p style={{ fontSize: 12, margin: '0 0 10px', color: 'rgba(0,0,0,0.6)' }}>
                  {isAr ? 'الملف: ' : 'File: '}<b dir="ltr">{pending.file.name}</b>
                  {' · '}
                  {isAr
                    ? 'يُرفع أولاً ثم تُقدَّم المرحلة، فالإلغاء الآن لا يرفع شيئاً.'
                    : 'It uploads first and then advances the stage, so cancelling now uploads nothing.'}
                </p>
              )}

              <p style={{ fontSize: 13, lineHeight: 1.7, margin: '0 0 12px', color: 'rgba(0,0,0,0.75)' }}>
                {sends ? (
                  isAr
                    ? <>سيصل <b>{traderName || 'التاجر'}</b> رسالة واتساب <b>فوراً</b> تخبره {summary?.ar}</>
                    : <>This sends <b>{traderName || 'the trader'}</b> a WhatsApp message <b>immediately</b>, telling them {summary?.en}</>
                ) : (
                  isAr
                    ? <>هذه المرحلة ليست متقدّمة عن الحالية، فالأرجح ألا تُرسَل أي رسالة (القاعدة تُحسب على أعلى مرحلة أُرسلت فعلاً، وهي غير ظاهرة هنا). ستُحدَّث حالة الطلب فقط.</>
                    : <>This stage is not ahead of the current one, so most likely no message is sent (the rule is measured against the highest stage actually sent, which isn’t visible here). The order status still updates.</>
                )}
              </p>

              <div style={{ background: 'rgba(0,0,0,0.035)', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.5)', marginBottom: 6 }}>
                  {isAr ? 'القالب والمتغيّرات التي ستُرسَل' : 'Template and parameters that will be sent'}
                </div>
                <div style={{ fontSize: 12.5, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}>
                  <div style={{ fontWeight: 700 }}>{template}</div>
                  {body.map(([name, value], i) => (
                    <div key={name} style={{ color: 'rgba(0,0,0,0.7)' }}>
                      {`{{${i + 1}}}`} {name} ={' '}
                      {value
                        ? <b>{value}</b>
                        : <i style={{ color: '#9A7B4F' }}>
                            {name === 'offer_count'
                              ? (isAr ? 'يُحسب عند الإرسال' : 'computed at send time')
                              : (isAr ? 'غير معروف هنا' : 'not known here')}
                          </i>}
                    </div>
                  ))}
                  {pending.key === 'out_for_delivery' && (
                    <div style={{ color: 'rgba(0,0,0,0.55)', marginTop: 4 }}>
                      + 2 buttons: track_token, request_id ({isAr ? 'يُحسبان عند الإرسال' : 'resolved at send time'})
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 10.5, color: 'rgba(0,0,0,0.45)', marginTop: 7, lineHeight: 1.6 }}>
                  {isAr
                    ? 'نص الرسالة نفسه معتمَد لدى المزوّد ولا نسخة منه هنا — عمداً، كي لا يتباعد عمّا يُرسَل فعلاً.'
                    : 'The message text itself is approved at the provider and deliberately not copied here, so it cannot drift from what is actually sent.'}
                </div>
              </div>

              {sends && (
                <p style={{ fontSize: 12, lineHeight: 1.7, margin: '0 0 14px', color: '#c0392b' }}>
                  {isAr
                    ? 'لا يمكن التراجع. والرجوع خطوة ثم التقدّم مرة أخرى لا يُرسل شيئاً — المرحلة تُحتسب مرسَلة، فالرسالة الخاطئة تبتلع الصحيحة.'
                    : 'This cannot be unsent. And stepping back then advancing again sends nothing — the stage counts as already sent, so a wrong message swallows the right one.'}
                </p>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button style={{ ...btn, background: 'transparent', color: 'rgba(0,0,0,0.6)', border: '1px solid rgba(0,0,0,0.15)' }}
                  onClick={() => setPending(null)}>
                  {isAr ? 'إلغاء' : 'Cancel'}
                </button>
                <button style={btn} disabled={busy === 'stage' || busy === 'video'}
                  onClick={() => { const p = pending; setPending(null); if (p.file) runVideo(p.file); else advance(p.stg); }}>
                  {pending.file
                    ? (isAr ? 'نعم، ارفع وأرسل' : 'Yes, upload and send')
                    : (isAr ? 'نعم، قدّم المرحلة' : 'Yes, advance')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Stage advancer */}
      <div style={card}>
        <h3 style={h}>{isAr ? 'مرحلة الطلب' : 'Order stage'}</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* `confirmed` is the trader's act, not ours: telr-verify writes it when
              they pay the offer. It stays VISIBLE so the admin can see where the
              order actually is — the panel mirrors the trader's tracker — but is
              not clickable, so it can never be reached by an admin.
              Index is paired before filtering: currentIdx is a position in
              MANAGED_STAGES, and filtering first would shift every stage after
              `confirmed` and highlight the wrong one. */}
          {MANAGED_STAGES.map((stg, i) => ({ stg, i })).map(({ stg, i }) => {
            const buyerDriven = stg.key === 'confirmed';
            // The current stage is not a target: clicking it again wrote another
            // event for the same stage (M-10113 got "وصل إليك" three times).
            const isCurrent = i === currentIdx;
            return (
              <button key={stg.key}
                onClick={buyerDriven || isCurrent ? undefined : () => setPending({ stg })}
                disabled={buyerDriven || isCurrent || busy === 'stage'}
                title={buyerDriven
                  ? (isAr ? 'تنتقل تلقائيًا عند دفع التاجر' : 'Set automatically when the trader pays')
                  : isCurrent ? (isAr ? 'المرحلة الحالية' : 'Current stage') : undefined}
                style={{ padding: '7px 11px', borderRadius: 8, fontSize: 12, fontFamily: FB,
                  cursor: buyerDriven ? 'default' : 'pointer',
                  borderStyle: buyerDriven && i !== currentIdx ? 'dashed' : 'solid',
                  borderWidth: i === currentIdx ? 0 : 1,
                  borderColor: 'rgba(0,0,0,0.15)',
                  background: i === currentIdx ? '#1a1814' : i < currentIdx ? 'rgba(47,93,58,0.1)' : 'transparent',
                  color: i === currentIdx ? '#fff' : i < currentIdx ? '#2d7a4f' : 'rgba(0,0,0,0.6)' }}>
                {i < currentIdx || (isCurrent && i === MANAGED_STAGES.length - 1) ? '✓ ' : ''}{isAr ? stg.ar : stg.en}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.4)', fontFamily: FB, margin: '8px 0 0' }}>
          {isAr ? 'اضغط مرحلة لتقديم الطلب إليها — يظهر للتاجر فورًا في المتتبّع. مرحلة "تم الاعتماد" تنتقل تلقائيًا عند دفع التاجر.' : 'Tap a stage to move the order there — the trader sees it instantly. "Confirmed" is set automatically when the trader pays.'}
        </p>
      </div>

      {/* Shipment tracking — only while the order is out for local delivery */}
      {atDelivery && (
        <div style={card}>
          <h3 style={h}>{isAr ? 'تتبّع الشحنة (اختياري)' : 'Shipment tracking (optional)'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lbl}>{isAr ? 'رقم التتبّع' : 'Tracking number'}</label>
              <input style={inp} value={trk.num} onChange={(e) => setTrk((p) => ({ ...p, num: e.target.value }))} placeholder={isAr ? 'مثال: 1234567890' : 'e.g. 1234567890'} />
            </div>
            <div>
              <label style={lbl}>{isAr ? 'الناقل' : 'Carrier'}</label>
              <select style={inp} value={trk.carrier} onChange={(e) => setTrk((p) => ({ ...p, carrier: e.target.value }))}>
                <option value="">{isAr ? '— اختر —' : '— Select —'}</option>
                {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={lbl}>{isAr ? 'رابط تتبّع يدوي (لناقل محلي غير مدرج)' : 'Manual tracking link (local carrier not listed)'}</label>
            <input style={inp} value={trk.url} onChange={(e) => setTrk((p) => ({ ...p, url: e.target.value }))} placeholder="https://…" dir="ltr" />
          </div>
          {trkPreview && (
            <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', fontFamily: FB, margin: '0 0 10px', wordBreak: 'break-all' }}>
              {isAr ? 'الرابط الذي سيصل التاجر: ' : 'Link the trader will get: '}
              <a href={trkPreview} target="_blank" rel="noreferrer" style={{ color: '#9A7B4F' }} dir="ltr">{trkPreview}</a>
            </p>
          )}
          <button style={btn} onClick={saveTracking} disabled={busy === 'tracking'}>
            {busy === 'tracking' ? '…' : (isAr ? 'احفظ التتبّع' : 'Save tracking')}
          </button>
          <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.4)', fontFamily: FB, margin: '8px 0 0' }}>
            {isAr ? 'اتركه فارغًا إن لم يتوفّر رقم — المرحلة تعمل بدونه.' : 'Leave blank if there is no number — the stage works without it.'}
          </p>
        </div>
      )}

      {/* Present offer */}
      <div style={card}>
        <h3 style={h}>{isAr ? 'تقديم العرض المنسّق' : 'Present curated offer'}</h3>
        <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <label style={{ ...lbl, flex: 2, marginBottom: 0 }}>{isAr ? 'الوصف' : 'Item'}<Star /></label>
          <label style={{ ...lbl, flex: 1, marginBottom: 0 }}>{isAr ? 'الكمية' : 'Qty'}<Star /></label>
          <label style={{ ...lbl, flex: 1, marginBottom: 0 }}>{isAr ? 'سعر (USD)' : 'Unit'}<Star /></label>
        </div>
        {rows.map((r, i) => {
          const re = offerErr.rows?.[i] || {};
          return (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
              <div style={{ flex: 2 }}>
                <input style={inp} placeholder={isAr ? 'الوصف' : 'Item'} value={r.desc} onChange={(e) => setRow(i, 'desc', e.target.value)} />
                {re.desc && <p style={errTxt}>{re.desc}</p>}
              </div>
              <div style={{ flex: 1 }}>
                <input style={inp} type="number" placeholder={isAr ? 'الكمية' : 'Qty'} value={r.qty} onChange={(e) => setRow(i, 'qty', e.target.value)} />
                {re.qty && <p style={errTxt}>{re.qty}</p>}
              </div>
              <div style={{ flex: 1 }}>
                <input style={inp} type="number" placeholder={isAr ? 'سعر (USD)' : 'Unit'} value={r.unit_price} onChange={(e) => setRow(i, 'unit_price', e.target.value)} />
                {re.unit_price && <p style={errTxt}>{re.unit_price}</p>}
              </div>
            </div>
          );
        })}
        <button onClick={() => setRows((r) => [...r, { desc: '', qty: '', unit_price: '' }])}
          style={{ background: 'none', border: '1px dashed rgba(0,0,0,0.2)', borderRadius: 8, padding: '5px 11px', fontSize: 12, cursor: 'pointer', fontFamily: FB, marginBottom: 12 }}>{isAr ? '+ سطر' : '+ Row'}</button>

        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>{isAr ? 'العملة' : 'Currency'}<Star /></label>
          <select style={{ ...inp, maxWidth: 120 }} value={currency} disabled><option>USD</option></select>
          {offerErr.currency && <p style={errTxt}>{offerErr.currency}</p>}
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>{isAr ? 'المورّد المصدر (للإدارة فقط — لا يراه التاجر)' : 'Source supplier (admin-only — the trader never sees it)'}</label>
          <select style={inp} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
            <option value="">{isAr ? '— غير محدد —' : '— none —'}</option>
            {sources.map((x) => (
              <option key={x.id} value={x.id}>
                {(x.company_name || x.full_name || x.id.slice(0, 8))}{x.maabar_supplier_id ? ` · ${x.maabar_supplier_id}` : ''} · {isVerifiedSupplierStatus(x.status) ? (isAr ? 'موثّق' : 'verified') : (x.status || '—')}
              </option>
            ))}
          </select>
          <p style={{ ...errTxt, color: sourceVerified ? '#2e7d4f' : '#b26a00' }}>
            {sourceVerified
              ? (isAr ? '✓ مورّد موثّق — يُحسب «مصنعًا موثّقًا» لرسوم الطلب.' : '✓ Verified supplier — counts as a verified factory for the request fee.')
              : (isAr ? 'بدون مورّد موثّق لا يُحسب العرض لرسوم الطلب (ضمان ١٠ أيام عمل).' : 'Without a verified supplier this offer does not count for the request fee (10-working-day promise).')}
          </p>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: '#9A7B4F', fontFamily: FB, margin: '4px 0 8px' }}>{isAr ? 'بروفايل المصنع (مجهول — بلا اسم)' : 'Factory profile (anonymized — no name)'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div><label style={lbl}>{isAr ? 'الموقع' : 'Location'}<Star /></label><input style={inp} value={fp.city} onChange={(e) => setF('city', e.target.value)} />{offerErr.city && <p style={errTxt}>{offerErr.city}</p>}</div>
          <div><label style={lbl}>{isAr ? 'الخبرة (سنة)' : 'Years'}</label><input style={inp} type="number" value={fp.years} onChange={(e) => setF('years', e.target.value)} /></div>
          <div><label style={lbl}>{isAr ? 'التقييم' : 'Rating'}</label><input style={inp} type="number" step="0.1" value={fp.rating} onChange={(e) => setF('rating', e.target.value)} /></div>
          <div><label style={lbl}>{isAr ? 'الطاقة الإنتاجية' : 'Capacity'}</label><input style={inp} value={fp.capacity} onChange={(e) => setF('capacity', e.target.value)} /></div>
          <div><label style={lbl}>{isAr ? 'أسواق التصدير' : 'Export markets'}</label><input style={inp} value={fp.export_markets} onChange={(e) => setF('export_markets', e.target.value)} /></div>
          <div><label style={lbl}>{isAr ? 'أقل كمية' : 'MOQ'}<Star /></label><input style={inp} type="number" value={fp.moq} onChange={(e) => setF('moq', e.target.value)} />{offerErr.moq && <p style={errTxt}>{offerErr.moq}</p>}</div>
          <div><label style={lbl}>{isAr ? 'مدة التسليم المتوقعة' : 'Est. delivery'}</label><input style={inp} value={fp.lead_time} onChange={(e) => setF('lead_time', e.target.value)} /></div>
          <div><label style={lbl}>{isAr ? 'شهادات الجودة (فاصلة)' : 'Quality certs (comma)'}</label><input style={inp} value={fp.certs} onChange={(e) => setF('certs', e.target.value)} placeholder="ISO 9001, BSCI" /></div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{isAr ? 'ملاحظات العرض' : 'Offer notes'}</label><textarea style={{ ...inp, resize: 'vertical' }} rows={2} value={offerNotes} onChange={(e) => setOfferNotes(e.target.value)} /></div>
        <button style={btn} onClick={presentOffer} disabled={busy === 'offer'}>{busy === 'offer' ? '…' : (isAr ? 'قدّم العرض للتاجر' : 'Present offer')}</button>
      </div>

      {/* Video + updates */}
      <div style={card}>
        <h3 style={h}>{isAr ? 'فيديو قبل الشحن + تحديثات' : 'Pre-shipping video + updates'}</h3>
        <label style={lbl}>{isAr ? 'رفع فيديو (يظهر للتاجر ويقدّم المرحلة)' : 'Upload video (shows to the trader + advances the stage)'}</label>
        <input type="file" accept="video/*" onChange={onVideo} disabled={busy === 'video'} style={{ fontSize: 12, marginBottom: 14, display: 'block' }} />

        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <select style={{ ...inp, maxWidth: 150 }} value={upStage} onChange={(e) => setUpStage(e.target.value)}>
            {MANAGED_STAGES.map((s) => <option key={s.key} value={s.key}>{isAr ? s.ar : s.en}</option>)}
          </select>
          <input style={{ ...inp, flex: 1 }} placeholder={isAr ? 'عنوان التحديث' : 'Update title'} value={upTitle} onChange={(e) => setUpTitle(e.target.value)} />
        </div>
        <textarea style={{ ...inp, resize: 'vertical', marginBottom: 8 }} rows={2} placeholder={isAr ? 'التفاصيل (تظهر للتاجر)' : 'Detail (shown to the trader)'} value={upBody} onChange={(e) => setUpBody(e.target.value)} />
        <button style={btn} onClick={addUpdate} disabled={busy === 'update'}>{isAr ? 'أضف تحديثًا' : 'Add update'}</button>
      </div>

      {/* Event log */}
      {events.length > 0 && (
        <div style={card}>
          <h3 style={h}>{isAr ? 'سجل الأحداث' : 'Event log'}</h3>
          {events.map((e) => (
            <div key={e.id} style={{ fontSize: 12, fontFamily: FB, color: 'rgba(0,0,0,0.65)', padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <span style={{ color: '#9A7B4F' }}>{e.stage || '—'}</span> · {e.kind === 'video' ? '🎥 ' : ''}{e.title || e.body}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
